import userService from "../services/userService";
import { CustomCommoditiesDocument, CustomPackagingsDocument } from "../components/configurator/CustomTypes";
import { CompositionsDocument } from "../model/compositions.types";
import { ActivesubstancesDocument } from "../model/activesubstances.types";
import { CommoditycategoriesDocument } from "../model/commoditycategories.types";
import { CommoditypropertiesDocument } from "../model/commodityproperties.types";
import { ColorsDocument } from "../model/colors.types";
import { CapsulesDocument } from "../model/capsules.types";
import { TabletsDocument } from "../model/tablets.types";

const version = 3;
const DATABASE = "main";
const COMMODITY_COLLECTION = "commodity";
const CONFIGURATION_COLLECTION = "configuration";
const PRICEGRAPH_COLLECTION = "priceGraph";
const COMMODITYSTATISTICS_COLLECTION = "commodityStatistics";

async function getPackaging(): Promise<Array<CustomPackagingsDocument>> {
  return await userService.getUser()?.callFunction("getPackaging", process.env.NODE_ENV);
}

async function getCommodities(): Promise<Array<CustomCommoditiesDocument>> {
  return await userService.getUser()?.callFunction("getCommodities", process.env.NODE_ENV);
}

// Interface for search commodity (lightweight)
export interface SearchCommodity {
  _id: any;
  title: { en: string };
  subtitle: { en: string };
}

// Fetch commodities directly from MongoDB collection for search
async function getCommoditiesForSearch(): Promise<Array<SearchCommodity>> {
  const user = userService.getUser();
  if (!user) {
    throw new Error("User not logged in");
  }

  const mongodb = user.mongoClient("mongodb-atlas");
  const collection = mongodb.db(DATABASE).collection(COMMODITY_COLLECTION);

  // Query only approved and not disabled commodities, return only needed fields
  const commodities = await collection.find(
    { disabled: false, approved: true },
    {
      projection: {
        _id: 1,
        title: 1,
        subtitle: 1
      }
    }
  );

  return commodities;
}

async function getCommodityProperties(): Promise<Array<CommoditypropertiesDocument>> {
  return await userService.getUser()?.callFunction("getCommodityProperties", process.env.NODE_ENV);
}

async function getCommodityCategories(): Promise<Array<CommoditycategoriesDocument>> {
  return await userService.getUser()?.callFunction("getCommodityCategories", process.env.NODE_ENV);
}

async function getCompositions(): Promise<Array<CompositionsDocument>> {
  return await userService.getUser()?.callFunction("getCompositions", process.env.NODE_ENV);
}

async function getActiveSubstances(): Promise<Array<ActivesubstancesDocument>> {
  return await userService.getUser()?.callFunction("getActiveSubstances", process.env.NODE_ENV);
}

async function getColors(): Promise<Array<ColorsDocument>> {
  return await userService.getUser()?.callFunction("getColors", process.env.NODE_ENV);
}

async function getCapsules(): Promise<Array<CapsulesDocument>> {
  return await userService.getUser()?.callFunction("getCapsules", process.env.NODE_ENV);
}

async function getTablets(): Promise<Array<TabletsDocument>> {
  return await userService.getUser()?.callFunction("getTablets", process.env.NODE_ENV);
}

async function getTrackingInformation(trackingId: string) {
  return userService.getUser()?.callFunction("getTrackingInformation", process.env.NODE_ENV, trackingId, version);
}

/**
 * Calls the createNewRequest custom function to create and insert a new request
 * @param request The order to process
 * @returns result of insert or false if something failed
 */
async function createNewRequest(request: any) {
  return await userService.getUser()?.callFunction("sendRequest", [process.env.NODE_ENV, request]);
}

// Interface for price ticker data
export interface PriceTickerCommodity {
  articleId: string;
  title: { en: string };
  subtitle: { en: string };
  commodityPrice: { date: string; price: number };
  change: number;
  svg: string;
}

// Default graph for fallback when no graph is available
const DEFAULT_GRAPH = `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="40"><path fill="none" stroke="#a1a5b7" stroke-width="1.5" d="M20,20L140,20"/></svg>`;

/**
 * Fetches price ticker data directly from MongoDB
 * Only returns commodities that are configured to show prices publicly
 */
async function getPriceTickerData(): Promise<Array<PriceTickerCommodity>> {
  console.log("[PriceTicker] Starting getPriceTickerData...");

  const user = userService.getUser();
  if (!user) {
    console.error("[PriceTicker] ERROR: User not logged in");
    throw new Error("User not logged in");
  }
  console.log("[PriceTicker] User logged in:", user.id);

  const mongodb = user.mongoClient("mongodb-atlas");

  // 1. Get anonymousConfiguration to find which commodities should show prices
  console.log("[PriceTicker] Fetching anonymousConfiguration...");
  const configCollection = mongodb.db(DATABASE).collection(CONFIGURATION_COLLECTION);
  const anonymousConfig = await configCollection.findOne({ key: "anonymousConfiguration" });

  console.log("[PriceTicker] anonymousConfig found:", !!anonymousConfig);
  console.log("[PriceTicker] anonymousConfig keys:", anonymousConfig ? Object.keys(anonymousConfig) : "N/A");
  console.log("[PriceTicker] anonymousConfig.values:", anonymousConfig?.values);

  if (!anonymousConfig || !anonymousConfig.values?.commodities?.length) {
    console.log("[PriceTicker] No commodities configured for public price display");
    return [];
  }

  const allowedCommodityIds: string[] = anonymousConfig.values.commodities;
  console.log("[PriceTicker] Allowed commodity IDs:", allowedCommodityIds.length, "- First 5:", allowedCommodityIds.slice(0, 5));

  // 2. Get all approved commodities with prices, then filter by allowed IDs
  console.log("[PriceTicker] Fetching all approved commodities...");
  const commodityCollection = mongodb.db(DATABASE).collection(COMMODITY_COLLECTION);
  const allCommodities = await commodityCollection.find(
    {
      disabled: false,
      approved: true,
    },
    {
      projection: {
        _id: 1,
        title: 1,
        subtitle: 1,
        fromPrice: 1,
      },
    }
  );

  console.log("[PriceTicker] All approved commodities fetched:", allCommodities?.length || 0);

  // Filter to only include commodities that are in the allowed list
  const commodities = allCommodities.filter((c: any) =>
    allowedCommodityIds.includes(c._id.toString())
  );

  console.log("[PriceTicker] Commodities after filtering by allowed IDs:", commodities.length);

  if (commodities.length > 0) {
    console.log("[PriceTicker] First commodity sample:", {
      id: commodities[0]._id.toString(),
      title: commodities[0].title,
      hasFromPrice: !!commodities[0].fromPrice,
      fromPrice: commodities[0].fromPrice,
    });
  }

  if (!commodities || commodities.length === 0) {
    console.log("[PriceTicker] No commodities found matching criteria");
    return [];
  }

  // 3. Get price graphs for these commodities
  const commodityIds = commodities.map((c: any) => c._id.toString());
  console.log("[PriceTicker] Fetching price graphs for", commodityIds.length, "commodities...");
  const priceGraphCollection = mongodb.db(DATABASE).collection(PRICEGRAPH_COLLECTION);
  const priceGraphs = await priceGraphCollection.find({
    article: { $in: commodityIds },
  });

  console.log("[PriceTicker] Price graphs fetched:", priceGraphs?.length || 0);

  // Create a map for quick lookup
  const priceGraphMap: { [key: string]: string } = {};
  priceGraphs.forEach((pg: any) => {
    priceGraphMap[pg.article] = pg.graph;
  });

  // 4. Get commodity statistics for price development
  console.log("[PriceTicker] Fetching commodity statistics...");
  const statisticsCollection = mongodb.db(DATABASE).collection(COMMODITYSTATISTICS_COLLECTION);
  const statistics = await statisticsCollection.find(
    { commodity: { $in: commodityIds } },
    { projection: { commodity: 1, priceDevelopment: 1 } }
  );

  console.log("[PriceTicker] Statistics fetched:", statistics?.length || 0);

  // Create a map for quick lookup
  const statisticsMap: { [key: string]: number } = {};
  statistics.forEach((stat: any) => {
    if (stat.priceDevelopment?.percentage !== undefined) {
      statisticsMap[stat.commodity] = stat.priceDevelopment.percentage;
    }
  });

  // 5. Build the result array
  const result: Array<PriceTickerCommodity> = [];
  let skippedNoPrice = 0;

  for (const commodity of commodities) {
    const id = commodity._id.toString();

    // Get the best price from fromPrice (lowest of warehouse, airfreight, seafreight)
    let price = -1;
    if (commodity.fromPrice && !commodity.fromPrice.outdated) {
      const prices = [
        commodity.fromPrice.warehouse?.price,
        commodity.fromPrice.airfreight?.price,
        commodity.fromPrice.seafreight?.price,
      ].filter((p) => p && p > 0);

      if (prices.length > 0) {
        price = Math.min(...prices);
      }
    }

    // Skip if no valid price
    if (price <= 0) {
      skippedNoPrice++;
      continue;
    }

    // Get graph or use default
    const graph = priceGraphMap[id] || DEFAULT_GRAPH;

    // Get price change percentage or default to 0
    const change = statisticsMap[id] ?? 0;

    result.push({
      articleId: id,
      title: { en: commodity.title?.en || "Unknown" },
      subtitle: { en: commodity.subtitle?.en || "" },
      commodityPrice: {
        date: new Date().toDateString(),
        price: price,
      },
      change: change,
      svg: graph,
    });
  }

  console.log("[PriceTicker] Skipped commodities (no valid price):", skippedNoPrice);
  console.log("[PriceTicker] Final result:", result.length, "commodities");

  if (result.length > 0) {
    console.log("[PriceTicker] First result sample:", result[0]);
  }

  return result;
}

// eslint-disable-next-line
export default {
  createNewRequest,
  getCommodities,
  getCommoditiesForSearch,
  getCommodityCategories,
  getCommodityProperties,
  getCompositions,
  getColors,
  getCapsules,
  getTablets,
  getTrackingInformation,
  getActiveSubstances,
  getPackaging,
  getPriceTickerData,
};
